import type { Game } from "../engine/game";
import type { AnimationLoader } from "./animationLoader";
import type { Camera } from "./camera";
import type { DrawableManager } from "./drawableManager";
import type { SpriteRenderer } from "./spriteRenderer";
import type { UserSettings } from "./userSettings";
import { dpr } from "./dpr";
import { calculateCellSize, computeCanvasLayout, type CanvasInsets } from "./canvasLayout";
import * as TileImageLoader from "./tileImageLoader";
import { playerSpriteComposer } from "./playerSpriteComposer";

export interface ResizeDeps {
	canvas: HTMLCanvasElement;
	camera: Camera;
	animationLoader: AnimationLoader;
	drawableManager: DrawableManager;
	spriteRenderer: SpriteRenderer;
	game: Game;
	userSettings: UserSettings;
	sidebarWidth: number;
	weaponBarHeight: number;
	healthBarHeight: number;
	canvasBottomMargin: number;
	onTileImagesRefreshed: (dashedSquareImg: HTMLCanvasElement, aimRedImg: HTMLCanvasElement) => void;
}

export class ResizeController {
	private readonly deps: ResizeDeps;
	private readonly insets: CanvasInsets;
	private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
	private resyncInProgress: boolean = false;
	private resyncPending: boolean = false;

	constructor(deps: ResizeDeps) {
		this.deps = deps;
		this.insets = {
			sidebarWidth: deps.sidebarWidth,
			weaponBarHeight: deps.weaponBarHeight,
			healthBarHeight: deps.healthBarHeight,
			canvasBottomMargin: deps.canvasBottomMargin,
		};
	}

	attach(): void {
		window.addEventListener("resize", this.resizeHandler);
	}

	dispose(): void {
		window.removeEventListener("resize", this.resizeHandler);
		if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
	}

	private resizeHandler = (): void => {
		if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => void this.applyLayout(), 150);
	};

	/** Resizes the canvas and recenters the camera — on window resize, and when the camera-follow setting changes. */
	applyLayout = async (): Promise<void> => {
		const { canvas, camera, game, userSettings } = this.deps;
		const ratio = dpr();
		const board = game.getCurrentBoard();
		const { cssWidth, cssHeight, cellSize } = computeCanvasLayout(
			board.width,
			board.height,
			ratio,
			this.insets,
			!userSettings.cameraFollowPlayer,
		);
		canvas.style.width = cssWidth + "px";
		canvas.style.height = cssHeight + "px";
		const gameArea = document.getElementById("game-area");
		if (gameArea) gameArea.style.width = cssWidth + "px";
		canvas.width = Math.round(cssWidth * ratio);
		canvas.height = Math.round(cssHeight * ratio);

		if (cellSize !== camera.cellWidth) {
			await this.performResync();
		}

		camera.setViewportSize(canvas.width, canvas.height);
		if (userSettings.cameraFollowPlayer) {
			camera.centerOn(game.player!.xy);
		} else {
			camera.centerOnBoard(board.width, board.height);
		}
	};

	private performResync = async (): Promise<void> => {
		if (this.resyncInProgress) {
			this.resyncPending = true;
			return;
		}
		this.resyncInProgress = true;

		const { canvas, camera, animationLoader, drawableManager, spriteRenderer, game } = this.deps;
		const currentBoard = game.getCurrentBoard();
		const currentCellSize = calculateCellSize(currentBoard.width, currentBoard.height, canvas.width, canvas.height);

		camera.setCellSize(currentCellSize, currentCellSize);
		animationLoader.setCellSize(currentCellSize, currentCellSize);
		TileImageLoader.clearCache();
		playerSpriteComposer.clearCache();
		drawableManager.clear();
		await drawableManager.syncWithBoard(currentBoard);
		await spriteRenderer.loadFloorTile();
		await spriteRenderer.syncFloor(currentBoard);
		const dashedSquareImg = await TileImageLoader.getImage("DashedSquare.png", camera.cellWidth, camera.cellHeight);
		const aimRedImg = await TileImageLoader.getImage("AimRed.png", camera.cellWidth, camera.cellHeight);
		this.deps.onTileImagesRefreshed(dashedSquareImg, aimRedImg);

		this.resyncInProgress = false;
		if (this.resyncPending) {
			this.resyncPending = false;
			const latestBoard = game.getCurrentBoard();
			const latestCellSize = calculateCellSize(latestBoard.width, latestBoard.height, canvas.width, canvas.height);
			if (latestCellSize !== camera.cellWidth) {
				this.performResync();
			}
		}
	};
}
